/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { FileText, Plus } from 'lucide-react';
import { useCallback, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type Session, type SessionStatus } from '../../lib/api/sessions.js';
import { useClients } from '../../lib/queries/clinical.js';
import { useSessions } from '../../lib/queries/sessions.js';
import { formatIst } from '../../lib/utils/datetime.js';
import { initialsOf } from '../../lib/utils/initials.js';

const STATUS_TONE: Readonly<Record<SessionStatus, BadgeTone>> = {
  scheduled: 'violet',
  in_progress: 'amber',
  completed: 'green',
  cancelled: 'slate',
  no_show: 'slate',
  client_late: 'amber',
  consultant_attended_client_absent: 'rose',
};

function NoteCard({
  session,
  clientName,
}: {
  readonly session: Session;
  readonly clientName: string;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Link
      to={`/therapist/sessions/${session.id}/note`}
      className="focus-ring block rounded-2xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-sm font-bold text-white">
            {initialsOf(clientName)}
          </span>
          <div>
            <p className="font-semibold text-slate-800 dark:text-slate-100">{clientName}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {formatIst(session.scheduledStart, 'dd MMM yyyy')}
              {session.sessionType !== null ? ` • ${session.sessionType}` : ''}
            </p>
          </div>
        </div>
        <Badge label={t(`status.${session.status}`)} tone={STATUS_TONE[session.status]} />
      </div>
      <div className="mt-4 flex items-center gap-1.5 text-sm font-medium text-[var(--accent-strong)]">
        <FileText className="h-4 w-4" aria-hidden="true" />
        {t('therapist.notes.open')}
      </div>
    </Link>
  );
}

function NewNoteModal({
  open,
  onOpenChange,
  sessions,
  clientNames,
  onPick,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly sessions: readonly Session[];
  readonly clientNames: ReadonlyMap<string, string>;
  readonly onPick: (id: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t('therapist.notes.newNoteTitle')}
      description={t('therapist.notes.newNotePrompt')}
      closeLabel={t('common.cancel')}
    >
      {sessions.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">
          {t('therapist.notes.noDocumentable')}
        </p>
      ) : (
        <ul className="max-h-80 space-y-2 overflow-y-auto">
          {sessions.map((s) => (
            <PickRow
              key={s.id}
              session={s}
              clientName={clientNames.get(s.clientId) ?? '—'}
              onPick={onPick}
            />
          ))}
        </ul>
      )}
    </Modal>
  );
}

function PickRow({
  session,
  clientName,
  onPick,
}: {
  readonly session: Session;
  readonly clientName: string;
  readonly onPick: (id: string) => void;
}): JSX.Element {
  const pick = useCallback(() => {
    onPick(session.id);
  }, [onPick, session.id]);
  return (
    <li>
      <button
        type="button"
        onClick={pick}
        className="focus-ring flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 text-left hover:border-[var(--accent)] dark:border-slate-700 dark:bg-slate-900"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-xs font-bold text-white">
          {initialsOf(clientName)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
            {clientName}
          </p>
          <p className="truncate text-xs text-slate-400">
            {formatIst(session.scheduledStart, 'dd MMM yyyy, h:mm a')}
            {session.sessionType !== null ? ` • ${session.sessionType}` : ''}
          </p>
        </div>
      </button>
    </li>
  );
}

/** Therapist → Session Notes (Figma). Note cards + a "New Note" session picker. */
export function TherapistNotesPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sessions = useSessions();
  const clients = useClients();
  const [picking, setPicking] = useState(false);

  const clientNames = useMemo(
    () => new Map((clients.data ?? []).map((c) => [c.id, c.fullName])),
    [clients.data],
  );

  // Notes are written against sessions that have started (in-progress/completed).
  const documentable = useMemo(
    () =>
      (sessions.data ?? []).filter((s) => s.status === 'in_progress' || s.status === 'completed'),
    [sessions.data],
  );

  const openPicker = useCallback(() => {
    setPicking(true);
  }, []);
  const onPick = useCallback(
    (id: string) => {
      setPicking(false);
      void navigate(`/therapist/sessions/${id}/note`);
    },
    [navigate],
  );

  return (
    <div className="p-8">
      <PageHeader
        title={t('therapist.notes.title')}
        subtitle={t('therapist.notes.count', { count: documentable.length })}
        action={
          <Button onClick={openPicker}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('therapist.notes.newNote')}
          </Button>
        }
      />
      <QueryState
        isLoading={sessions.isLoading}
        isError={sessions.isError}
        isEmpty={documentable.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('therapist.notes.empty')}
      >
        <div className="grid gap-4 lg:grid-cols-2">
          {documentable.map((s) => (
            <NoteCard key={s.id} session={s} clientName={clientNames.get(s.clientId) ?? '—'} />
          ))}
        </div>
      </QueryState>

      <NewNoteModal
        open={picking}
        onOpenChange={setPicking}
        sessions={documentable}
        clientNames={clientNames}
        onPick={onPick}
      />
    </div>
  );
}
